import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { addCalendarDays, businessDaysBetween, daysBetween, NOTICE_WINDOW_CALENDAR_DAYS } from '../lib/util';
import { StatusBadge } from '../components/StatusBadge';
import { DeadlineIndicator } from '../components/DeadlineIndicator';
import { VettingSlaCell } from '../components/VettingSlaCell';
import { getCurrentUser } from '../auth/useUser';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { isNotifiableDecision, notificationContactLabel, useLifecycleActions } from '../state/actions/lifecycle';

export function Dashboard() {
  const { cases, stats, now } = useAppData();
  const { setPage, setSelectedCase, setModal, openCase, setCaseTab } = useUIState();
  const { closeCureWindow, receiveAppeal, recordCure, initiateReVet, syncToSettlement } = useLifecycleActions();
  const user = getCurrentUser()!;
  const canApprove = user.canApprove;

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4, color: C.text }}>BCID Portal Dashboard</h2>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 24 }}>382 Communications — Authorized Onboarding Agent & Vetting Agent</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        {[
          { label: 'Total Cases', value: stats.total, color: C.text },
          { label: 'In Review', value: stats.inReview, color: C.info },
          { label: 'Successful', value: stats.successful, color: C.success },
          { label: 'Unsuccessful', value: stats.unsuccessful, color: C.danger },
          { label: 'Pending Notification', value: stats.pendingNotification, color: C.warn },
          { label: 'Cure Windows Open', value: stats.cureWindowOpen, color: C.warn },
          { label: 'Active Appeals', value: stats.appealsActive, color: C.warn },
          { label: 'Re-Vets Due (30d)', value: stats.revetsDue, color: C.goldText },
          { label: 'Pending Sync', value: stats.pendingSync, color: C.info },
          { label: 'Onboarding Queue', value: stats.onboardingSubmitted, color: C.warn },
        ].map((s, i) => (
          <div key={i} style={styles.statBox}>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {(() => {
        const oaSubmitted = cases.filter(c => c.onboarding?.status === 'SUBMITTED');
        const oaRecertDue = cases.filter(c => c.onboarding?.recertDueDate && c.onboarding?.recertStatus === 'PENDING' && daysBetween(now, c.onboarding.recertDueDate) <= 30);
        const ipUrgent = cases.flatMap(c => (c.ipDisputes || []).filter((d: any) => d.status === 'RECEIVED').map((d: any) => ({ ...d, caseId: c.id, registrant: c.registrant })));
        const shaftPending = cases.flatMap(c => (c.shaftDisputes || []).filter((d: any) => d.status === 'FLAGGED').map((d: any) => ({ ...d, caseId: c.id, registrant: c.registrant })));
        const hasOaItems = oaSubmitted.length + oaRecertDue.length + ipUrgent.length + shaftPending.length > 0;
        return hasOaItems ? (
          <div style={{ ...styles.card, borderLeft: `3px solid ${C.gold}` }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: C.goldText }}>Onboarding Agent — Action Items</h3>
            {oaSubmitted.map(c => (
              <div key={c.id + '-oa'} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <span style={{ color: C.warn }}>◫</span> <strong>{c.onboarding?.data?.legalName || c.registrant}</strong> — Application submitted, awaiting review
                  {c.onboarding?.oaReviewDeadline && <DeadlineIndicator deadline={c.onboarding.oaReviewDeadline} label="OA review (10 biz days)" now={now} />}
                </div>
                <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => { openCase(c, 'onboarding'); setCaseTab('onboarding'); }}>Review</button>
              </div>
            ))}
            {ipUrgent.map((d: any) => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <span style={{ color: C.danger }}>©</span> <strong>{d.registrant}</strong> — IP claim received, 24hr action required
                  <DeadlineIndicator deadline={d.notifyDeadline} label="24hr notify + suspend" now={now} />
                </div>
                <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => setPage('ip-disputes')}>Handle Claim</button>
              </div>
            ))}
            {shaftPending.map((d: any) => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <span style={{ color: C.warn }}>⚠</span> <strong>{d.registrant}</strong> — SHAFT content flagged, registrant notification due
                  <DeadlineIndicator deadline={d.oaNotifyDeadline} label="OA notify (10 biz days)" now={now} />
                </div>
                <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => setPage('ip-disputes')}>View Disputes</button>
              </div>
            ))}
            {oaRecertDue.map(c => (
              <div key={c.id + '-recert'} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <span style={{ color: C.goldText }}>↻</span> <strong>{c.registrant}</strong> — Annual recertification due
                  <DeadlineIndicator deadline={c.onboarding?.recertDueDate} label="Recertification" now={now} />
                </div>
                <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => { openCase(c, 'onboarding'); setCaseTab('onboarding'); }}>Review</button>
              </div>
            ))}
          </div>
        ) : null;
      })()}
      <div style={styles.card}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: C.goldText }}>Vetting Agent — Action Items</h3>
        {cases.filter(c => isNotifiableDecision(c.decision) && !c.notificationSentAt).map(c => (
          <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
            <div>
              <span style={{ color: C.warn }}>⚠</span> <strong>{c.registrant}</strong> — Decision made, notification not sent
              <DeadlineIndicator deadline={c.decisionDate ? addCalendarDays(c.decisionDate, NOTICE_WINDOW_CALENDAR_DAYS).toISOString() : null} label="30-day notification deadline" now={now} />
            </div>
            <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => { setSelectedCase(c); setModal('send-notification'); }}>Send Notification</button>
          </div>
        ))}
        {cases.filter(c => c.cureStatus === 'OPEN' && c.cureDeadline && businessDaysBetween(now, c.cureDeadline) <= 3).map(c => (
          <div key={c.id + '-cure'} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
            <div>
              <span style={{ color: C.danger }}>●</span> <strong>{c.registrant}</strong> — Cure window expiring
              <DeadlineIndicator deadline={c.cureDeadline} label="Cure deadline" now={now} />
            </div>
            {/* #626 — the three cure-window actions, in the Cure & Appeals
                page's order and with its reasoning (#587): a cure and a
                dispute are two events discriminated by which button an
                approver presses, so a surface that offers only the appeal is
                a surface where a registrant who sent the missing document
                gets recorded as having disputed the finding. This row is the
                one an approver reaches first — it is on the dashboard, filtered
                to windows inside three business days of expiry — so the
                missing half mattered most here. Cure first because it is the
                outcome the window exists to produce; Close last and outlined
                because it is the destructive one. */}
            <div style={{ display: 'flex', gap: 8 }}>
              {canApprove && <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={() => recordCure(c.id)}>Record Cure</button>}
              {canApprove && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => receiveAppeal(c.id)}>Record Appeal</button>}
              {canApprove && <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => closeCureWindow(c.id)}>Close (No Response)</button>}
            </div>
          </div>
        ))}
        {cases.filter(c => c.settlementSyncStatus === 'PENDING_SYNC').map(c => (
          <div key={c.id + '-sync'} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
            <div>
              <span style={{ color: C.info }}>●</span> <strong>{c.registrant}</strong> — Pending Settlement Platform sync
              {c.settlementChangeDeadline && <DeadlineIndicator deadline={c.settlementChangeDeadline} label="24hr sync deadline" now={now} />}
            </div>
            <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => syncToSettlement(c.id)}>Sync Now</button>
          </div>
        ))}
        {cases.filter(c => c.revetStatus === 'SCHEDULED' && c.revetDueAt && daysBetween(now, c.revetDueAt) <= 30).map(c => (
          <div key={c.id + '-revet'} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
            <div>
              <span style={{ color: C.goldText }}>●</span> <strong>{c.registrant}</strong> — Annual re-vet approaching
              <DeadlineIndicator deadline={c.revetDueAt} label="Re-vet due" now={now} />
            </div>
            <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => initiateReVet(c.id)}>Initiate Re-Vet</button>
          </div>
        ))}
        {stats.pendingNotification + stats.cureWindowOpen + stats.pendingSync + stats.revetsDue === 0 && (
          <div style={{ color: C.textMuted, fontSize: 13, padding: 12 }}>No pending action items — all clear ✓</div>
        )}
      </div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: C.goldText }}>Recent Cases</h3>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Case</th><th style={styles.th}>Registrant</th><th style={styles.th}>Status</th><th style={styles.th}>Authorizer</th><th style={styles.th}>SLA</th><th style={styles.th}>Settlement</th>
          </tr></thead>
          <tbody>
            {cases.slice(0, 10).map(c => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => openCase(c)}>
                <td style={styles.td}><button type="button" style={{ ...styles.btnUnstyled, color: C.goldText, fontWeight: 600 }} onClick={e => { e.stopPropagation(); openCase(c); }}>{c.id}</button></td>
                <td style={styles.td}>{c.registrant}</td>
                <td style={styles.td}><StatusBadge status={c.status} /></td>
                {/* #693 — the registrant's authorizer, not the Onboarding
                    Agent: 382 is the OA on every case, so the old column said
                    the same thing on every row. Falls back to the registrant
                    when the submission names no authorizer. */}
                <td style={styles.td}><span style={{ color: C.textMuted, fontSize: 12 }}>{notificationContactLabel(c)}</span></td>
                <td style={styles.td}><VettingSlaCell c={c} now={now} /></td>
                <td style={styles.td}>{c.settlementSyncStatus ? <StatusBadge status={c.settlementSyncStatus} /> : <span style={{ color: C.textDim }}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
